import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import LandingPage from "@/pages/LandingPage";
import Login from "@/pages/Login";
import SignUp from "@/pages/SignUp";
import Catalog from "@/pages/Catalog";
import MyLibrary from "@/pages/MyLibrary";
import GiftMembership from "@/pages/GiftMembership";
import RentalCart from "@/pages/RentalCart";
import BookDetails from "@/pages/BookDetails";
import AdminDashboard from "@/pages/AdminDashboard";
import Profile from "@/pages/Profile";
import FAQs from "@/pages/FAQs";
import Events from "@/pages/Events";
import Contact from "@/pages/Contact";
import Policies from "@/pages/Policies";
import ResetPassword from "@/pages/ResetPassword";
import NotFound from "@/pages/NotFound";
import { AuthProvider, useAuth } from "@/context/AuthContext";

function AuthLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50" data-testid="auth-loader">
      <div className="text-center">
        <div className="inline-block w-10 h-10 border-4 border-amber-700 border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-sm text-amber-800 font-medium">Loading your library…</p>
      </div>
    </div>
  );
}

function PaymentsRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('section', 'payments');
  return <Navigate to={`/my-library?${params.toString()}`} replace />;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <AuthLoader />;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  return children;
}

function StaffRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <AuthLoader />;
  if (!user || user.role !== "staff") return <Navigate to="/my-library" replace />;
  return children;
}

function AuthOnlyRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <AuthLoader />;
  if (user) return <Navigate to={location.state?.from || (user.role === "staff" ? "/admin" : "/my-library")} replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public — designed pages */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/faqs" element={<FAQs />} />
      <Route path="/events" element={<Events />} />
      <Route path="/contact" element={<Contact />} />

      {/* Auth */}
      <Route path="/login" element={<AuthOnlyRoute><Login /></AuthOnlyRoute>} />
      <Route path="/signup" element={<AuthOnlyRoute><SignUp /></AuthOnlyRoute>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/policies" element={<Policies />} />
      <Route path="/policies/:policyType" element={<Policies />} />

      {/* Member-protected (unchanged — will be redesigned in later batches) */}
      <Route path="/home" element={<Navigate to="/" replace />} />
      <Route path="/explore" element={<Navigate to="/catalog" replace />} />
      <Route path="/catalog" element={<Catalog />} />
      <Route path="/my-library" element={<ProtectedRoute><MyLibrary /></ProtectedRoute>} />
      <Route path="/payments" element={<PaymentsRedirect />} />
      <Route path="/gift" element={<GiftMembership />} />
      <Route path="/rental-cart" element={<ProtectedRoute><RentalCart /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/recently-added" element={<Navigate to="/catalog" replace />} />
      <Route path="/book/:bookId" element={<BookDetails />} />
      <Route path="/admin" element={<StaffRoute><AdminDashboard /></StaffRoute>} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
